import React, { useState, useEffect, useCallback } from "react";
import { Download, RefreshCw, ThumbsUp, ThumbsDown, ChevronDown, ChevronUp } from "lucide-react";
import { API_HOST } from "../constants";
import { Link } from "react-router-dom";

interface FeedbackItem {
  id: string;
  created_at: string;
  user_message: string;
  assistant_message: string;
  context_type: string;
  mode: string;
  rating: string | null;
  correction: string | null;
  lesson_id?: string;
  course_slug?: string;
  lesson_title?: string;
  task_id?: string;
  task_title?: string;
}

export const AITutorValidate = () => {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterRating, setFilterRating] = useState<string>("");
  const [filterContext, setFilterContext] = useState<string>("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingCorrection, setEditingCorrection] = useState<{ id: string; value: string } | null>(null);
  const [exportFormat, setExportFormat] = useState<"jsonl" | "json">("jsonl");
  const [exportRating, setExportRating] = useState<string>("good");

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterRating) params.set("rating", filterRating);
      if (filterContext) params.set("context_type", filterContext);
      const res = await fetch(`${API_HOST}/api/ai/feedback?${params}`);
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) {
      console.error(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filterRating, filterContext]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleUpdateRating = async (id: string, rating: string) => {
    try {
      await fetch(`${API_HOST}/api/ai/feedback/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating }),
      });
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, rating } : it))
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleUpdateCorrection = async (id: string, correction: string) => {
    try {
      await fetch(`${API_HOST}/api/ai/feedback/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ correction: correction || null }),
      });
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, correction: correction || null } : it))
      );
      setEditingCorrection(null);
    } catch (e) {
      console.error(e);
    }
  };

  const handleExport = async (format: "jsonl" | "json", rating: string = "good") => {
    try {
      const params = new URLSearchParams({
        format,
        rating: rating === "all" ? "all" : rating || "good",
        use_correction: "true",
      });
      const res = await fetch(`${API_HOST}/api/ai/feedback/export?${params}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ai-tutor-finetuning-${rating}-${new Date().toISOString().slice(0, 10)}.${format === "jsonl" ? "jsonl" : "json"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-light pt-24 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div>
            <Link
              to="/ai-tutor"
              className="text-sm text-primary hover:underline mb-1 inline-block"
            >
              ← AI Tutor
            </Link>
            <h1 className="text-2xl font-heading font-semibold text-neutral-text">
              Валидация ответов AI Tutor
            </h1>
            <p className="text-sm text-neutral-text/60 mt-1">
              Оценка и правка ответов для fine-tuning
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={fetchItems}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-border bg-white hover:bg-neutral-light text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Обновить
            </button>
            <select
              value={format}
              onChange={(e) => setExportFormat(e.target.value)}
              className="px-3 py-2 rounded-lg border border-neutral-border bg-white text-sm"
            >
              <option value="jsonl">JSONL</option>
              <option value="json">JSON</option>
            </select>
            <select
              value={exportRating}
              onChange={(e) => setExportRating(e.target.value)}
              className="px-3 py-2 rounded-lg border border-neutral-border bg-white text-sm"
            >
              <option value="good">Только хорошие</option>
              <option value="bad">Только плохие</option>
              <option value="all">Все</option>
            </select>
            <button
              onClick={() => handleExport(exportFormat, exportRating)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary-dark text-sm"
            >
              <Download className="w-4 h-4" />
              Экспорт
            </button>
          </div>
        </div>

        <div className="flex gap-3 mb-6 flex-wrap">
          <select
            value={filterRating}
            onChange={(e) => setFilterRating(e.target.value)}
            className="px-3 py-2 rounded-lg border border-neutral-border bg-white text-sm"
          >
            <option value="">Все оценки</option>
            <option value="good">Хорошие</option>
            <option value="bad">Плохие</option>
          </select>
          <select
            value={filterContext}
            onChange={(e) => setFilterContext(e.target.value)}
            className="px-3 py-2 rounded-lg border border-neutral-border bg-white text-sm"
          >
            <option value="">Все контексты</option>
            <option value="generic">generic</option>
            <option value="lesson">lesson</option>
            <option value="course">course</option>
            <option value="editor">editor</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-12 text-neutral-text/60">Загрузка…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-neutral-border">
            <p className="text-neutral-text/60">Нет записей для валидации.</p>
            <p className="text-sm text-neutral-text/50 mt-1">
              Используйте AI Tutor и оценивайте ответы (👍/👎) — они появятся здесь.
            </p>
            <Link to="/ai-tutor" className="text-primary hover:underline mt-2 inline-block">
              Открыть AI Tutor →
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <div
                key={it.id}
                className="bg-white rounded-xl border border-neutral-border overflow-hidden"
              >
                <div
                  className="p-4 cursor-pointer hover:bg-neutral-light/50"
                  onClick={() => setExpandedId(expandedId === it.id ? null : it.id)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-neutral-text truncate">
                        {it.user_message}
                      </p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-neutral-text/50">
                        <span>{it.context_type}</span>
                        <span>•</span>
                        <span>{it.mode}</span>
                        {it.lesson_title && (
                          <>
                            <span>•</span>
                            <span>{it.lesson_title}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUpdateRating(it.id, "good");
                        }}
                        className={`p-1.5 rounded ${
                          it.rating === "good"
                            ? "text-green-600 bg-green-50"
                            : "text-neutral-text/40 hover:bg-neutral-light"
                        }`}
                        title="Хороший"
                      >
                        <ThumbsUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUpdateRating(it.id, "bad");
                        }}
                        className={`p-1.5 rounded ${
                          it.rating === "bad"
                            ? "text-red-600 bg-red-50"
                            : "text-neutral-text/40 hover:bg-neutral-light"
                        }`}
                        title="Плохой"
                      >
                        <ThumbsDown className="w-4 h-4" />
                      </button>
                      {expandedId === it.id ? (
                        <ChevronUp className="w-4 h-4 text-neutral-text/50" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-neutral-text/50" />
                      )}
                    </div>
                  </div>
                </div>
                {expandedId === it.id && (
                  <div className="border-t border-neutral-border p-4 bg-neutral-light/30 space-y-3">
                    <div>
                      <p className="text-xs font-medium text-neutral-text/60 mb-1">Вопрос</p>
                      <p className="text-sm text-neutral-text whitespace-pre-wrap bg-white p-3 rounded border">
                        {it.user_message}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-neutral-text/60 mb-1">Ответ AI</p>
                      <div className="text-sm text-neutral-text whitespace-pre-wrap bg-white p-3 rounded border max-h-48 overflow-y-auto">
                        {it.assistant_message}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-neutral-text/60 mb-1">
                        Исправление (для fine-tuning)
                      </p>
                      {editingCorrection?.id === it.id ? (
                        <div className="space-y-2">
                          <textarea
                            value={editingCorrection.value}
                            onChange={(e) =>
                              setEditingCorrection({ ...editingCorrection, value: e.target.value })
                            }
                            className="w-full px-3 py-2 text-sm border rounded resize-none min-h-[100px]"
                            placeholder="Идеальный ответ..."
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() =>
                                handleUpdateCorrection(it.id, editingCorrection.value)
                              }
                              className="text-sm px-3 py-1.5 bg-primary text-white rounded hover:bg-primary-dark"
                            >
                              Сохранить
                            </button>
                            <button
                              onClick={() => setEditingCorrection(null)}
                              className="text-sm px-3 py-1.5 text-neutral-text/70 hover:bg-neutral-light rounded"
                            >
                              Отмена
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="text-sm whitespace-pre-wrap bg-white p-3 rounded border min-h-[60px] cursor-pointer hover:border-primary/40"
                          onClick={() =>
                            setEditingCorrection({
                              id: it.id,
                              value: it.correction || it.assistant_message,
                            })
                          }
                        >
                          {it.correction || (
                            <span className="text-neutral-text/50">Нажмите, чтобы добавить исправление</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
